import { Box, Typography } from "@mui/material";
import { ArrowDownwardIcon, ArrowRightIcon } from "assets/styles/icons";
import { useProgress } from "context/ProgressProvider";
import dayjs from "dayjs";
import React, { ReactElement, useEffect } from "react";
import { useSelector } from "react-redux";
import Utils from "shared/helpers/Utils";

const Accordion: React.FC<{
  expanded: boolean;
  onChange: (event: React.MouseEvent, isExpanded: boolean) => void;
  children: React.ReactNode;
}> = ({ expanded, onChange, children }) => {
  return (
    <Box
      sx={{
        "&:last-child": {
          borderBottom: "none",
        },
        borderBottom: "1px solid #e0e0e0",
      }}
    >
      {React.Children.map(children, (child) => {
        if (React.isValidElement(child)) {
          return React.cloneElement(child as React.ReactElement<any>, {
            expanded,
            onChange,
          });
        }
        return child;
      })}
    </Box>
  );
};

const AccordionSummary: React.FC<{
  children: React.ReactNode;
  expandIcon?: React.ReactNode;
  "aria-controls"?: string;
  id?: string;
  expanded?: boolean;
  onChange?: (event: React.MouseEvent, isExpanded: boolean) => void;
}> = ({ children, expandIcon, expanded = false, onChange, ...props }) => {
  return (
    <Box
      style={{
        padding: "0 6px",
        cursor: "pointer",
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        background: "white",
        transition: "background-color 0.2s ease",
        border: "none",
        outline: "none",
      }}
      onClick={(e) => onChange && onChange(e, !expanded)}
      {...props}
    >
      <Box sx={{ fontSize: "16px", fontWeight: 500, color: "#333" }}>
        {children}
      </Box>
      <Box sx={{ color: "#666", transition: "transform 0.2s ease" }}>
        {expandIcon}
      </Box>
    </Box>
  );
};

const AccordionDetails: React.FC<{
  children: React.ReactNode;
  expanded?: boolean;
}> = ({ children, expanded = false }) => {
  return (
    <Box
      style={{
        maxHeight: expanded ? "4000px" : "0",
        overflow: "hidden",
        transition: "max-height 0.3s ease",
        background: "white",
      }}
    >
      <Box sx={{ padding: "12px 6px 0 6px" }}>{children}</Box>
    </Box>
  );
};

const ExpandMoreIcon: React.FC<{ expanded?: boolean }> = ({
  expanded = false,
}) => {
  return <ArrowDownwardIcon expanded={expanded} />;
};

const DetailRow: React.FC<{
  label: string;
  value: string | ReactElement | number;
  showArrow?: boolean;
  onClick?: any;
  blockEdit?: boolean;
}> = ({ label, value, showArrow = true, onClick, blockEdit = false }) => {
  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        padding: "12px",
        cursor: blockEdit ? "not-allowed" : "pointer",
        transition: "background-color 0.2s ease",
        border: "1px solid #E5E5E5",
        borderRadius: "12px",
        marginTop: "12px",
        "&:first-child": {
          marginTop: "0",
        },
      }}
      onClick={onClick}
    >
      <Box sx={{ flex: 1 }}>
        <Typography
          sx={{ fontSize: "13px", color: "#474747", marginBottom: "4px" }}
        >
          {label}
        </Typography>
        <Typography
          sx={{ fontSize: "16px", color: "#0A0A0A", marginBottom: "4px" }}
        >
          {value}
        </Typography>
      </Box>
      {showArrow && (
        <Box sx={{ color: "#0A0A0A", marginLeft: "16px" }}>
          <ArrowRightIcon />
        </Box>
      )}
    </Box>
  );
};

const AccordionReviewComponents = ({
  expanded,
  handleChange,
  setShowDialog,
  setExistingLoanStatus,
}) => {
  const formatLoanText = (text) => {
    return text
      .split("-")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  const { currentProfile, isBroker } = useProgress();
  const isMultiProperties = currentProfile.properties.length > 1 ? true : false;

  const handleClickProperties = (data) => {
    console.log("data", data);
    setShowDialog(data);
  };

  return (
    <>
      <Box
        sx={{
          padding: "12px",
          border: "1px solid #E5E5E5",
          borderRadius: "16px",
        }}
      >
        <Accordion
          expanded={expanded === "panel1"}
          onChange={handleChange("panel1")}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon expanded={expanded === "panel1"} />}
            aria-controls="panel1bh-content"
            id="panel1bh-header"
          >
            <Typography variant="h3">
              {isBroker ? "Customer details" : "Your details"}
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <DetailRow
              label="Legal name"
              value={`${currentProfile.firstName} ${currentProfile.lastName}`}
              onClick={() => setShowDialog("details-1")}
            />
            <DetailRow
              label="Email address"
              value={currentProfile.email}
              // onClick={() => setShowDialog("details-2")}
              blockEdit
            />
            <DetailRow
              label="Phone number"
              value={currentProfile.phoneNumber}
              onClick={() => setShowDialog("details-3")}
            />
          </AccordionDetails>
        </Accordion>
      </Box>

      {/* Your loan */}
      <Box
        sx={{
          padding: "12px",
          border: "1px solid #E5E5E5",
          borderRadius: "16px",
        }}
        mt={4}
      >
        <Accordion
          expanded={expanded === "panel2"}
          onChange={handleChange("panel2")}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon expanded={expanded === "panel2"} />}
            aria-controls="panel1bh-content"
            id="panel1bh-header"
          >
            <Typography variant="h3">
              {isBroker ? "Customer loan" : "Your loan"}
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <DetailRow
              label="Loan amount"
              value={`$${Utils.formatCurrency(currentProfile.loanAmount)} AUD`}
              onClick={() => setShowDialog("loan-1")}
            />
            <DetailRow
              label="Loan term"
              value={currentProfile.loanTerm}
              onClick={() => setShowDialog("loan-2")}
            />
            <DetailRow
              label="Funding time"
              value={currentProfile.expectedTime}
              onClick={() => setShowDialog("loan-3")}
            />
            {currentProfile.expectedTime === "I have a specific date" && (
              <DetailRow
                label="Expected loan date"
                value={dayjs(currentProfile.expectedLoanDate).format(
                  "YYYY-MM-DD",
                )}
                onClick={() => setShowDialog("loan-4")}
              />
            )}

            <DetailRow
              label="Preferred repayment"
              value={currentProfile.repaymentMethod}
              onClick={() => setShowDialog("loan-5")}
            />
            <DetailRow
              label="Loan purpose"
              value={
                (currentProfile.loanPurpose &&
                  formatLoanText(currentProfile.loanPurpose)) ||
                "-"
              }
              onClick={() => setShowDialog("loan-6")}
            />
            <DetailRow
              label="Loan Payback"
              value={
                (currentProfile.loanPayBackOption &&
                  formatLoanText(currentProfile.loanPayBackOption)) ||
                "-"
              }
              onClick={() => setShowDialog("loan-7")}
            />
            {isBroker && (
              <DetailRow
                label="Broker Commission Rate"
                value={`${currentProfile.brokerCommissionRate}%` || "0"}
                onClick={() => setShowDialog("loan-8")}
              />
            )}
          </AccordionDetails>
        </Accordion>
      </Box>

      {/* Your Business */}
      <Box
        sx={{
          padding: "12px",
          border: "1px solid #E5E5E5",
          borderRadius: "16px",
        }}
        mt={4}
      >
        <Accordion
          expanded={expanded === "panel3"}
          onChange={handleChange("panel3")}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon expanded={expanded === "panel3"} />}
            aria-controls="panel1bh-content"
            id="panel1bh-header"
          >
            <Typography variant="h3">
              {isBroker ? "Customer business" : "Your business"}
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <DetailRow
              label="Entity type"
              value={
                (currentProfile.entityType &&
                  formatLoanText(currentProfile.entityType)) ||
                "-"
              }
              onClick={() => setShowDialog("business-1")}
            />
            <DetailRow
              label="Entity name"
              value={
                (currentProfile.legalEntityType &&
                  formatLoanText(currentProfile.legalEntityType)) ||
                "-"
              }
              onClick={() => setShowDialog("business-2")}
            />
            <DetailRow
              label="ABN"
              value={currentProfile.abn}
              onClick={() => setShowDialog("business-3")}
            />
            <DetailRow
              label="Registered entity name"
              value={currentProfile.registeredEntityName}
              onClick={() => setShowDialog("business-4")}
            />
            <DetailRow
              label="Entity address"
              value={currentProfile.entityAddress}
              onClick={() => setShowDialog("business-5")}
            />
          </AccordionDetails>
        </Accordion>
      </Box>

      {/* Property details */}
      <Box
        sx={{
          padding: "12px",
          border: "1px solid #E5E5E5",
          borderRadius: "16px",
        }}
        mt={4}
      >
        <Accordion
          expanded={expanded === "panel4"}
          onChange={handleChange("panel4")}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon expanded={expanded === "panel4"} />}
            aria-controls="panel1bh-content"
            id="panel1bh-header"
            // style={{overflowY: "auto"}}
          >
            <Typography variant="h3">Property details</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <DetailRow
              label="Number of properties"
              value={currentProfile.noOfProperties || "-"}
              onClick={() => handleClickProperties("property-1")}
            />
            <DetailRow
              label="Property type"
              value={
                isMultiProperties ? (
                  <>
                    {currentProfile.properties.map((prop, index) => (
                      <Box className="flex justify-start align-center">
                        <Typography
                          variant="h3"
                          className="p-1"
                          sx={{ minWidth: "5rem" }}
                        >
                          Property {index + 1}
                        </Typography>

                        <Typography
                          variant="h3"
                          className="justify-center px-3"
                        >
                          :
                        </Typography>
                        <Typography variant="body1" className="p-1">
                          {prop.propertyType}
                        </Typography>
                      </Box>
                    ))}
                  </>
                ) : (
                  currentProfile.propertyType
                )
              }
              onClick={() => handleClickProperties("property-2")}
            />
            <DetailRow
              label="Property address"
              // value={
              //   // isMultiProperties
              //   //   ? currentProfile.properties
              //   //       .map(
              //   //         (prop, index) =>
              //   //           `Property${index + 1}: ${prop.propertyAddress}`
              //   //       )
              //   //       .filter((type) => type !== "")
              //   //       .join(", ")
              //   //   : currentProfile.propertyAddress
              // }
              value={
                isMultiProperties ? (
                  <>
                    {currentProfile.properties.map((prop, index) => (
                      <Box className="flex justify-start align-center">
                        <Typography
                          variant="h3"
                          className="p-1"
                          sx={{ minWidth: "5rem" }}
                        >
                          Property {index + 1}
                        </Typography>

                        <Typography
                          variant="h3"
                          className="justify-center px-3"
                        >
                          :
                        </Typography>
                        <Typography variant="body1" className="p-1">
                          {prop.propertyAddress}
                        </Typography>
                      </Box>
                    ))}
                  </>
                ) : (
                  currentProfile.propertyAddress
                )
              }
              onClick={() => handleClickProperties("property-3")}
            />
            <DetailRow
              label="Estimated property amount"
              // value={
              //   isMultiProperties
              //     ? currentProfile.properties
              //         .map(
              //           (prop, index) =>
              //             `Property${index + 1}: ${`$${Utils.formatCurrency(
              //               prop.estimatedPropertyAmount
              //             )} AUD`}`
              //         )
              //         .filter((type) => type !== "")
              //         .join(", ")
              //     : `$${Utils.formatCurrency(
              //         currentProfile.estimatedPropertyAmount || 0
              //       )} AUD`
              // }
              value={
                isMultiProperties ? (
                  <>
                    {currentProfile.properties.map((prop, index) => (
                      <Box className="flex justify-start align-center">
                        <Typography
                          variant="h3"
                          className="p-1"
                          sx={{ minWidth: "5rem" }}
                        >
                          Property {index + 1}
                        </Typography>

                        <Typography
                          variant="h3"
                          className="justify-center px-3"
                        >
                          :
                        </Typography>

                        {!prop.estimatedPropertyAmount ? (
                          "-"
                        ) : (
                          <Typography variant="body1" className="p-1">
                            $
                            {Utils.formatCurrency(prop.estimatedPropertyAmount)}{" "}
                            AUD{" "}
                          </Typography>
                        )}
                      </Box>
                    ))}
                  </>
                ) : (
                  <>
                    $
                    {Utils.formatCurrency(
                      currentProfile.estimatedPropertyAmount || 0,
                    )}{" "}
                    AUD
                  </>
                )
              }
              onClick={() => {
                setExistingLoanStatus("Add");
                handleClickProperties("property-4");
              }}
            />
            <DetailRow
              label="Any existing loan"
              // value={
              //   isMultiProperties
              //     ? currentProfile.properties
              //         .map((prop, index) => {
              //           if (typeof prop.isAnyExistingLoan === "boolean") {
              //             return `Property${index + 1}: ${
              //               prop.isAnyExistingLoan ? "Yes" : "No"
              //             }`;
              //           }
              //           return prop.isAnyExistingLoan;
              //         })
              //         .filter((type) => type !== "")
              //         .join(", ")
              //     : currentProfile.isAnyExistingLoan
              //     ? "Yes"
              //     : "No"
              // }
              value={
                isMultiProperties ? (
                  <>
                    {currentProfile.properties.map((prop, index) => (
                      <Box className="flex justify-start align-center">
                        <Typography
                          variant="h3"
                          className="p-1"
                          sx={{ minWidth: "5rem" }}
                        >
                          Property {index + 1}
                        </Typography>

                        <Typography
                          variant="h3"
                          className="justify-center px-3"
                        >
                          :
                        </Typography>
                        <Typography variant="body1" className="p-1">
                          {prop.isAnyExistingLoan ? "Yes" : "No"}
                        </Typography>
                      </Box>
                    ))}
                  </>
                ) : (
                  <>{currentProfile.isAnyExistingLoan ? "Yes" : "No"}</>
                )
              }
              onClick={() => {
                setExistingLoanStatus("Add");
                handleClickProperties("property-5");
              }}
            />
            {!isMultiProperties && !currentProfile.isAnyExistingLoan ? (
              <></>
            ) : (
              <>
                <DetailRow
                  label="Existing loan settlement"
                  // value={
                  //   isMultiProperties
                  //     ? currentProfile.properties
                  //         .map(
                  //           (prop, index) =>
                  //             `Property${index + 1}: ${
                  //               prop.existingLoanSettlementOption || "-"
                  //             }`
                  //         )
                  //         .filter((type) => type !== "")
                  //         .join(", ")
                  //     : currentProfile.existingLoanSettlementOption || "-"
                  // }
                  value={
                    isMultiProperties ? (
                      <>
                        {currentProfile.properties.map((prop, index) => (
                          <Box className="flex justify-start align-center">
                            <Typography
                              variant="h3"
                              className="p-1"
                              sx={{ minWidth: "5rem" }}
                            >
                              Property {index + 1}
                            </Typography>

                            <Typography
                              variant="h3"
                              className="justify-center px-3"
                            >
                              :
                            </Typography>
                            <Typography variant="body1" className="p-1">
                              {!prop.isAnyExistingLoan ? (
                                <i style={{ color: "gray" }}>
                                  There's no existing loan
                                </i>
                              ) : (
                                prop.existingLoanSettlementOption || "-"
                              )}
                            </Typography>
                          </Box>
                        ))}
                      </>
                    ) : (
                      <>
                        {!currentProfile.isAnyExistingLoan ? (
                          <i style={{ color: "gray" }}>
                            There's no existing loan
                          </i>
                        ) : (
                          currentProfile.existingLoanSettlementOption || "-"
                        )}
                      </>
                    )
                  }
                  onClick={() => handleClickProperties("property-6")}
                />
                <DetailRow
                  label="Existing loan amount"
                  // value={
                  //   isMultiProperties
                  //     ? currentProfile.properties
                  //         .map(
                  //           (prop, index) =>
                  //             `Property${index + 1}: ${
                  //               !prop.existingLoanAmount
                  //                 ? "-"
                  //                 : `$${Utils.formatCurrency(
                  //                     prop.existingLoanAmount
                  //                   )} AUD`
                  //             }`
                  //         )
                  //         .filter((type) => type !== "")
                  //         .join(", ")
                  //     : `$${Utils.formatCurrency(
                  //         currentProfile.existingLoanAmount || 0
                  //       )} AUD`
                  // }
                  value={
                    isMultiProperties ? (
                      <>
                        {currentProfile.properties.map((prop, index) => (
                          <Box className="flex justify-start align-center">
                            <Typography
                              variant="h3"
                              className="p-1"
                              sx={{ minWidth: "5rem" }}
                            >
                              Property {index + 1}
                            </Typography>

                            <Typography
                              variant="h3"
                              className="justify-center px-3"
                            >
                              :
                            </Typography>
                            {!prop.isAnyExistingLoan ? (
                              <i
                                style={{
                                  color: "gray",
                                  fontSize: "14px",
                                  fontWeight: 400,
                                }}
                              >
                                There's no existing loan
                              </i>
                            ) : !prop.existingLoanAmount ? (
                              "-"
                            ) : (
                              <Typography variant="body1" className="p-1">
                                ${" "}
                                {Utils.formatCurrency(prop.existingLoanAmount)}{" "}
                                AUD
                              </Typography>
                            )}
                          </Box>
                        ))}
                      </>
                    ) : (
                      <>
                        ${" "}
                        {Utils.formatCurrency(
                          currentProfile.existingLoanAmount || 0,
                        )}{" "}
                        AUD
                      </>
                    )
                  }
                  onClick={() => handleClickProperties("property-7")}
                />
              </>
            )}

            <DetailRow
              label="Any caveats"
              // value={
              //   isMultiProperties
              //     ? currentProfile.properties
              //         .map((prop, index) => {
              //           if (typeof prop.isAnyCaveats === "boolean") {
              //             return `Property${index + 1}: ${
              //               prop.isAnyCaveats ? "Yes" : "No"
              //             }`;
              //           }
              //           return prop.isAnyCaveats;
              //         })
              //         .filter((type) => type !== "")
              //         .join(", ")
              //     : currentProfile.isAnyCaveats
              //     ? "Yes"
              //     : "No"
              // }
              value={
                isMultiProperties ? (
                  <>
                    {currentProfile.properties.map((prop, index) => (
                      <Box className="flex justify-start align-center">
                        <Typography
                          variant="h3"
                          className="p-1"
                          sx={{ minWidth: "5rem" }}
                        >
                          Property {index + 1}
                        </Typography>

                        <Typography
                          variant="h3"
                          className="justify-center px-3"
                        >
                          :
                        </Typography>
                        <Typography variant="body1" className="p-1">
                          {prop.isAnyCaveats ? "Yes" : "No"}
                        </Typography>
                      </Box>
                    ))}
                  </>
                ) : (
                  <>{currentProfile.isAnyCaveats ? "Yes" : "No"}</>
                )
              }
              onClick={() => handleClickProperties("property-8")}
            />
          </AccordionDetails>
        </Accordion>
      </Box>
    </>
  );
};

export default AccordionReviewComponents;
